/**
 * Shared constants and utilities for Analysis feature components.
 */
import {
  IconTargetArrow,
  IconDropletHalf2Filled,
  IconTestPipe,
  IconSparkles,
  IconCalendarEvent,
} from '@tabler/icons-react';
import type { TablerIcon } from '@tabler/icons-react';
import {
  ANALYSIS_SERIES_COLORS,
  EVENT_LINE_COLOR as CENTRALIZED_EVENT_LINE_COLOR,
  CHART_FALLBACK_COLOR,
  MAINTENANCE_CATEGORY_COLORS,
} from '@/utils/chartColors';

/** Gray color for event lines - subtle appearance */
export const EVENT_LINE_COLOR = CENTRALIZED_EVENT_LINE_COLOR;

/** Dash pattern for event lines */
export const EVENT_LINE_DASHARRAY = '3 3';

/** Color palette for charts - consistent colors for visual distinction */
export const CHART_COLORS = ANALYSIS_SERIES_COLORS;

/** Parameter display configuration - maps param keys to display labels and units */
export const PARAMETER_CONFIG: Record<string, { label: string; unit: string }> = {
  alkalinity: { label: 'Alkalinity', unit: 'dKH' },
  ca: { label: 'Calcium', unit: 'ppm' },
  mg: { label: 'Magnesium', unit: 'ppm' },
  no3: { label: 'Nitrate', unit: 'ppm' },
  po4: { label: 'Phosphate', unit: 'ppm' },
  k: { label: 'Potassium', unit: 'ppm' },
  nh3: { label: 'Ammonia', unit: 'ppm' },
  temp: { label: 'Temperature', unit: '\u00B0C' },
  ph: { label: 'pH', unit: '' },
  salinity: { label: 'Salinity', unit: 'ppt' },
  ph_aquawiz: { label: 'pH (AquaWiz)', unit: '' },
  dKH: { label: 'Alkalinity', unit: 'dKH' },
  KH: { label: 'Alkalinity', unit: 'dKH' },
};

/**
 * Get display configuration for a parameter.
 * Falls back to using the parameter key as label if not found.
 */
export function getParameterConfig(paramKey: string): { label: string; unit: string } {
  return PARAMETER_CONFIG[paramKey] || { label: paramKey, unit: '' };
}

/**
 * Maps measurement param keys to the `parameterId` used by tank target ranges
 * (see DEFAULT_PARAMETERS in types/settings.ts). Params without an entry have
 * no target range.
 */
export const TARGET_RANGE_KEYS: Record<string, string> = {
  temp: 'temperature',
  ph: 'ph',
  ph_aquawiz: 'ph',
  salinity: 'salinity',
  alkalinity: 'alkalinity',
  ca: 'calcium',
  mg: 'magnesium',
  no3: 'nitrate',
  po4: 'phosphate',
};

/**
 * Data sources with sparse/infrequent data points.
 * These sources get visible dot indicators on chart lines
 * so users can see where actual measurements were recorded.
 */
export const SPARSE_DATA_SOURCES = new Set(['manual', 'icp']);

/** Mapping of device source codes to display names */
export const SOURCE_DISPLAY_NAMES: Record<string, string> = {
  mastertronic: 'Mastertronic',
  focustronic: 'Focustronic',
  dosetronic: 'Dosetronic',
  aquawiz: 'AquaWiz KH1',
  apex: 'Apex Fusion',
  apex_fusion: 'Apex Fusion',
  aquarimate: 'Aquarimate',
  manual: 'Manual',
  alkatronic: 'Alkatronic',
  icp: 'ICP',
};

/**
 * Get friendly display name for a device source.
 */
export function getSourceDisplayName(source: string): string {
  return SOURCE_DISPLAY_NAMES[source] || source;
}

/**
 * Parse a composite parameter key into param and source components.
 * Example: "alkalinity:aquawiz" -> { param: "alkalinity", source: "aquawiz" }
 */
export function parseParameterKey(compositeKey: string): { param: string; source: string | undefined } {
  const colonIndex = compositeKey.indexOf(':');
  if (colonIndex === -1) {
    return { param: compositeKey, source: undefined };
  }
  return {
    param: compositeKey.substring(0, colonIndex),
    source: compositeKey.substring(colonIndex + 1),
  };
}

/**
 * Format a parameter key for display in the sidebar.
 * Shows "Param Label - Source Name" for composite keys.
 */
export function formatParameterLabel(compositeKey: string): string {
  const { param, source } = parseParameterKey(compositeKey);
  const config = getParameterConfig(param);

  if (!source) {
    return config.label;
  }

  return `${config.label} - ${getSourceDisplayName(source)}`;
}

/**
 * Result of grouping parameters by base name.
 */
export interface ParameterGroup {
  /** Base parameter name (e.g., "alkalinity") */
  param: string;
  /** All composite keys for this parameter (e.g., ["alkalinity:aquawiz", "alkalinity:mastertronic"]) */
  keys: string[];
}

/**
 * Group parameter keys by their base name.
 *
 * Example:
 *   Input: ["alkalinity:aquawiz", "alkalinity:mastertronic", "ca:mastertronic"]
 *   Output: [
 *     { param: "alkalinity", keys: ["alkalinity:aquawiz", "alkalinity:mastertronic"] },
 *     { param: "ca", keys: ["ca:mastertronic"] }
 *   ]
 */
export function groupParametersByName(items: string[]): ParameterGroup[] {
  const groupMap = new Map<string, string[]>();
  const order: string[] = [];

  for (const item of items) {
    const { param } = parseParameterKey(item);
    if (!groupMap.has(param)) {
      groupMap.set(param, []);
      order.push(param);
    }
    groupMap.get(param)!.push(item);
  }

  return order.map((param) => ({
    param,
    keys: groupMap.get(param)!,
  }));
}

// --- Event Category Configuration ---

export interface EventCategoryConfig {
  icon: TablerIcon;
  color: string;
}

/** Keyword-based event categories. Order matters - first match wins. */
const EVENT_CATEGORIES: { keywords: string[]; config: EventCategoryConfig }[] = [
  { keywords: ['calibrat'], config: { icon: IconTargetArrow, color: MAINTENANCE_CATEGORY_COLORS.calibration ?? CHART_FALLBACK_COLOR } },
  { keywords: ['water change'], config: { icon: IconDropletHalf2Filled, color: MAINTENANCE_CATEGORY_COLORS.water_change ?? CHART_FALLBACK_COLOR } },
  { keywords: ['dose', 'dosing'], config: { icon: IconTestPipe, color: MAINTENANCE_CATEGORY_COLORS.dosing ?? CHART_FALLBACK_COLOR } },
  { keywords: ['clean', 'soak'], config: { icon: IconSparkles, color: MAINTENANCE_CATEGORY_COLORS.cleaning ?? CHART_FALLBACK_COLOR } },
];

/** Default config for events that don't match any category */
export const DEFAULT_EVENT_CONFIG: EventCategoryConfig = {
  icon: IconCalendarEvent,
  color: CHART_FALLBACK_COLOR,
};

/**
 * Get icon and color for an event based on keyword matching against its name.
 * Falls back to a generic calendar icon for unrecognized events.
 */
export function getEventConfig(eventName: string): EventCategoryConfig {
  const lower = eventName.toLowerCase();
  for (const category of EVENT_CATEGORIES) {
    if (category.keywords.some((kw) => lower.includes(kw))) {
      return category.config;
    }
  }
  return DEFAULT_EVENT_CONFIG;
}
